import { useEffect, useRef, useState } from 'react';

import { Box, Typography } from '@mui/material';
import BigNumber from 'bignumber.js';

import { useWeb3 } from '@/hooks/use-web3';
import {
  COIN_NAME_SFL,
  GETPOOLDETAILS,
  GETREWARDSCHEDULECOUNT,
  GETSTAKE,
  GETSTAKECOUNT,
  REWARDSCHEDULE,
} from '@/lib/common/name-space';
import { promiseAllSettled } from '@/lib/Promise';
import { ResClaimList } from '@/types';
import { NumWithComma, trimTrailingZeros } from '@/lib/BigNumbers';
import { useWeb3AuthConnect } from '@web3auth/modal/react';
import { useTickerStore } from '@/store/zustand/ticker';

export default function ClaimList({ contractAddr }: { contractAddr: string }) {
  const [list, setList] = useState<ResClaimList[] | null>(null);
  const [expectReward, setExpectReward] = useState('0');
  const [sumAmt, setSumAmt] = useState('0');
  const rewardCnt = useRef(0);
  const address = useRef<string>('');

  const { web3authCall, address: addr } = useWeb3();
  const { isConnected } = useWeb3AuthConnect();
  const { getSymbolData } = useTickerStore();

  useEffect(() => {
    if (list && list?.length > 0) {
      return;
    }
    (async () => {
      if (isConnected && addr) {
        address.current = addr;
        const arr = [() => stateCount(), () => scheduleCount()];
        const counts = await promiseAllSettled(arr);
        rewardCnt.current = counts[1];
        const { merge, sum } = await mergeData(counts);

        setList(merge);
        setSumAmt(sum);
        setExpectReward(await calTotalFundAndInterestRate(sum));
      }
    })();
    return () => {};
  }, [web3authCall, isConnected, addr, contractAddr]);

  // 1. 풀에 스테이킹한 횟수
  const stateCount = async (): Promise<string> => {
    return web3authCall!.callStaking(GETSTAKECOUNT, contractAddr, [address.current]);
  };
  // 2. 보상 스케줄 전체 리스트 개수
  const scheduleCount = async (): Promise<string> => {
    return web3authCall!.callStaking(GETREWARDSCHEDULECOUNT, contractAddr);
  };
  // 3. 보상스케줄 및 사용자 스테이크 정보
  const mergeData = async (aSchedule: string[]): Promise<{ merge: ResClaimList[]; sum: string }> => {
    let arrClaimSchedule = [];
    let arrStakeInfo = [];
    let arrSumStakeInfo = []; // 사용자 스테이킹 합계 구하기
    const arrResult = [];
    for (let y = 0; y < Number(aSchedule[0]); y++) {
      arrStakeInfo.push(() => web3authCall!.callStaking(GETSTAKE, contractAddr, [address.current, String(y)]));
      for (let i = 0; i < Number(aSchedule[1]); i++) {
        arrClaimSchedule.push(() => web3authCall!.callStaking(REWARDSCHEDULE, contractAddr, String(i)));
      }
      const claimSchedule = await promiseAllSettled(arrClaimSchedule);
      const stakeInfo = await promiseAllSettled(arrStakeInfo);
      arrSumStakeInfo.push(stakeInfo[0]);
      const temp = claimSchedule.map((item: any) => ({
        ...item,
        ...stakeInfo[0],
        index: BigNumber(stakeInfo[0].amountStaked).plus(Math.random()),
      }));

      arrResult.push(temp);
      arrClaimSchedule = [];
      arrStakeInfo = [];
    }
    // 스테이킹 합계 구하기
    const sum =
      arrSumStakeInfo.length > 0 &&
      arrSumStakeInfo.reduce((acc, item) => {
        return BigNumber(acc).plus(item.amountStaked);
      }, BigNumber(0));
    const re = arrResult.flat();

    return { merge: re, sum: sum ? trimTrailingZeros(sum.toFixed(6, 1)) : '0' };
  };
  // 30일 기준 예상 리워드
  const calTotalFundAndInterestRate = async (aSumAmt: string) => {
    const arr = [() => web3authCall!.callStaking(GETPOOLDETAILS, contractAddr)];
    const data = await promiseAllSettled(arr);
    const [details] = data;

    const sfl = getSymbolData(COIN_NAME_SFL); // await ticker({ symbol: COIN_NAME_SFL, amount: '1' });

    const sflUsdt = BigNumber(aSumAmt)
      .multipliedBy(sfl?.usdt || '0')
      .toFixed(8, 1);
    const interestMonth = monthInterest(details.annualInterestMultipliedRate, sflUsdt);
    const re = expectRewardAmt(sfl?.usdt || '0', interestMonth);
    return re === 'NaN' ? '0' : re || '0';
  };

  // 월 이자 구하기
  const monthInterest = (aAnnualRate: string, aTotalFund: string) => {
    const yearRate = BigNumber(aTotalFund).multipliedBy(BigNumber(aAnnualRate).dividedBy(100)).toFixed(8, 1);
    const dailyRate = BigNumber(yearRate).dividedBy(365).toFixed(8, 1);
    const interestMonth = BigNumber(dailyRate).multipliedBy(30).toFixed(8, 1);
    return interestMonth;
  };

  // 예상 수량
  const expectRewardAmt = (aSflUsdt: string, aInterestMonth: string) => {
    return trimTrailingZeros(BigNumber(aInterestMonth).dividedBy(aSflUsdt).toFixed(6, 1));
  };
  return (
    <>
      <Box
        sx={{
          maxWidth: '1680px',
          padding: '16px 24px',
          marginTop: '16px',
          marginBottom: '38px',
        }}
      >
        <Box>
          <Typography>
            ● Currently, your cumulative staking amount is{' '}
            <Typography component="span" color="primary" sx={{ fontWeight: 'bold' }}>
              {trimTrailingZeros(NumWithComma(sumAmt))}
            </Typography>{' '}
            SFL, and the estimated reward reflecting the current market price is{' '}
            <Typography component="span" color="primary" sx={{ fontWeight: 'bold' }}>
              {trimTrailingZeros(NumWithComma(expectReward))}
            </Typography>{' '}
            SFL.
            <br /> However, the rewards you will receive when proceeding with the actual claim will be reflected
            according to the SFL market price.
            <br />
            <br />● Staking status can be checked after achieving the staking fundraising goal.
          </Typography>
        </Box>
        {/* {isMobile ? (
          <>
            {rewardCnt.current > 0 && (
              <>
                <Typography variant="h3" color={theme.greyscale.darkgrey2} sx={{ marginBottom: '16px' }}>
                  Reward Detail
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    setIsOpenClaimModal(true);
                  }}
                >
                  Claim All Reward
                </Button>
                <Box mb={3} />
              </>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {list && list.map((item: ResClaimList) => <PortfolioStakedCard key={item.index} item={item} />)}
            </Box>
          </>
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid xs={8}>
                <Typography variant="h3" color={theme.greyscale.darkgrey2} sx={{ marginBottom: '16px' }}>
                  Reward Detail
                </Typography>
              </Grid>
              <Grid xs>
                {rewardCnt.current > 0 && (
                  <CommonButton
                    sx={{ width: '100%', padding: '12px 20px', borderRadius: '10px' }}
                    text="Claim All Reward"
                    handleClick={() => {
                      setIsOpenClaimModal(true);
                    }}
                    variant="h6"
                    color={'#ffffff'}
                    bgColor={theme.background.blue}
                    border={theme.background.blue}
                  />
                )}
              </Grid>
            </Grid>
            {list && <PortfolioDetailTable list={list} columns={portfolioStakedDetailColumns} />}
          </>
        )} */}
      </Box>
      {/* <ClaimModal open={isOpenClaimModal} handleClose={() => setIsOpenClaimModal(false)} contractAddr={contractAddr} /> */}
    </>
  );
}
